import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { leadsAPI } from '../services/api';
import { formatCurrency, formatDate, formatDateTime } from '../utils/currency';
import { formatPhoneNumber } from '../utils/validation';
import { Plus, Search, Flame, Users, DollarSign, Calendar, Edit, Trash2, Phone, Mail, ArrowLeft, UserPlus, PhoneCall, CheckCircle, XCircle, ArrowRight, Clock, AlertCircle, Loader2, RefreshCw, Download, FileSpreadsheet, FileText, Snowflake } from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import TimePicker from '../components/TimePicker';
import usePermissions from '../hooks/usePermissions';
import useFinancialVisibility from '../hooks/useFinancialVisibility';
import { useOffline } from '../context/OfflineContext';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Debounce hook for search optimization
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

// Event Type Options
const EVENT_TYPE_OPTIONS = [
  { value: 'WEDDING', label: 'Wedding' },
  { value: 'RECEPTION', label: 'Reception' },
  { value: 'ENGAGEMENT', label: 'Engagement' },
  { value: 'BIRTHDAY', label: 'Birthday' },
  { value: 'BABY_SHOWER', label: 'Baby Shower' },
  { value: 'OTHERS', label: 'Others' }
];

const Leads = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [leadToConvert, setLeadToConvert] = useState(null);
  const [bookingType, setBookingType] = useState('confirmed');
  const [isSubmitting, setIsSubmitting] = useState(false); // Prevent double submissions
  const [updateStatusOpen, setUpdateStatusOpen] = useState(false);
  const [leadToUpdate, setLeadToUpdate] = useState(null);
  const [statusUpdateData, setStatusUpdateData] = useState({
    status: '',
    progressNote: '',
    nextFollowupDate: ''
  });
  const { toast } = useToast();
  const { canCreateLeads, canEditLeads, canDeleteLeads, canExportLeads } = usePermissions();
  const hideFinancials = useFinancialVisibility();
  const { isOffline, checkOfflineAction } = useOffline();
  
  // Follow-up leads modal state
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
  const [selectedFollowUpLead, setSelectedFollowUpLead] = useState(null);
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [statusUpdateDialogOpen, setStatusUpdateDialogOpen] = useState(false);
  const [newStatusForFollowUp, setNewStatusForFollowUp] = useState('');
  const [followUpProgressNote, setFollowUpProgressNote] = useState('');
  
  // Real-time field validation errors
  const [fieldErrors, setFieldErrors] = useState({});

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    eventType: 'WEDDING',
    customEventType: '', // For "Others" option
    bookingDateFrom: '',
    bookingDateTo: '',
    bookingTimeFrom: '',
    bookingTimeTo: '',
    followupDate: '',
    expectedGuests: '',
    budget: '',
    status: 'new',
    priority: 'warm',
    source: '',
    notes: '',
    assignedTo: ''
  });

  // Debounce search query for better performance
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Calculate leads needing follow-up this month
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const followUpLeadsThisMonth = leads.filter(l => {
    if (!l.followupDate || l.status === 'won' || l.status === 'lost') return false;
    const followUpDate = new Date(l.followupDate);
    return followUpDate.getMonth() === currentMonth && followUpDate.getFullYear() === currentYear;
  }).sort((a, b) => new Date(a.followupDate) - new Date(b.followupDate));

  useEffect(() => {
    loadLeads();
  }, []);

  useEffect(() => {
    filterLeads();
  }, [leads, debouncedSearchQuery, statusFilter, priorityFilter]);

  // Handle edit query parameter from MonthlyLeads page
  useEffect(() => {
    const editLeadId = searchParams.get('edit');
    if (editLeadId && leads.length > 0) {
      const leadToEdit = leads.find(l => l.id === editLeadId);
      if (leadToEdit) {
        handleEdit(leadToEdit);
        // Clear the query parameter after opening edit dialog
        setSearchParams({});
      }
    }
  }, [searchParams, leads]);

  const loadLeads = async () => {
    try {
      const data = await leadsAPI.getAll();
      setLeads(data || []);
    } catch (error) {
      console.error('Failed to load leads:', error);
      toast({
        title: 'Error',
        description: 'Failed to load leads. Please try again.',
        variant: 'destructive'
      });
      setLeads([]);
    }
  };

  // Memoized filter function for better performance
  const filterLeads = useCallback(() => {
    let filtered = [...leads];

    if (statusFilter !== 'all') {
      filtered = filtered.filter(l => l.status === statusFilter);
    }

    if (priorityFilter !== 'all') {
      filtered = filtered.filter(l => l.priority === priorityFilter);
    }

    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase();
      filtered = filtered.filter(l => 
        (l.name && l.name.toLowerCase().includes(query)) ||
        (l.email && l.email.toLowerCase().includes(query)) ||
        (l.phone && l.phone.toLowerCase().includes(query)) ||
        (l.id && l.id.toLowerCase().includes(query))
      );
    }

    setFilteredLeads(filtered);
  }, [leads, debouncedSearchQuery, statusFilter, priorityFilter]);

  // Real-time field validation
  const validateField = (fieldName, value, allFormData = formData) => {
    let error = '';
    
    switch (fieldName) {
      case 'name':
        if (!value || value.trim() === '') {
          error = 'Client name is required';
        } else if (value.trim().length < 2) {
          error = 'Name must be at least 2 characters';
        }
        break;
        
      case 'phone':
        if (!value || value.trim() === '') {
          error = 'Phone number is required';
        } else {
          const digits = value.replace(/\D/g, '');
          if (digits.length !== 10) {
            error = 'Phone must be exactly 10 digits';
          }
        }
        break;
        
      case 'email':
        if (value && value.trim() !== '') {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            error = 'Invalid email format';
          }
        }
        break;
        
      case 'bookingDateFrom':
        if (!value) {
          error = 'From date is required';
        }
        break;
        
      case 'bookingDateTo':
        if (value && allFormData.bookingDateFrom) {
          if (new Date(value) < new Date(allFormData.bookingDateFrom)) {
            error = 'To date must be after From date';
          }
        }
        break;
        
      case 'expectedGuests':
        if (value && value !== '') {
          const count = Number(value);
          if (isNaN(count) || count < 0) {
            error = 'Cannot be negative';
          }
        }
        break;
        
      case 'budget':
        if (value && value !== '') {
          const amount = Number(value);
          if (isNaN(amount) || amount < 0) {
            error = 'Cannot be negative';
          }
        }
        break;
        
      case 'customEventType':
        if (allFormData.eventType === 'OTHERS' && (!value || value.trim() === '')) {
          error = 'Please specify the event type';
        }
        break;
        
      default:
        break;
    }
    
    return error;
  };

  // Handle field change with real-time validation
  const handleFieldChange = (fieldName, value) => {
    const newFormData = { ...formData, [fieldName]: value };
    setFormData(newFormData);
    
    // Validate the field
    const error = validateField(fieldName, value, newFormData);
    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
    
    // Also validate related fields
    if (fieldName === 'bookingDateFrom' && newFormData.bookingDateTo) {
      const toError = validateField('bookingDateTo', newFormData.bookingDateTo, newFormData);
      setFieldErrors(prev => ({ ...prev, bookingDateTo: toError }));
    }
    if (fieldName === 'eventType') {
      const customError = validateField('customEventType', newFormData.customEventType, newFormData);
      setFieldErrors(prev => ({ ...prev, customEventType: customError }));
    }
  };

  // Validate field on blur
  const handleFieldBlur = (fieldName) => {
    const error = validateField(fieldName, formData[fieldName]);
    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submissions
    if (isSubmitting) return;
    
    setIsSubmitting(true); // Disable button
    
    try {

      // Map form data to backend expected format
      const leadData = {
        name: formData.name,
        phone: formData.phone.replace(/\s/g, ''), // Remove spaces
      };

      // Validation: Check for negative values
      if (formData.expectedGuests && parseInt(formData.expectedGuests) < 0) {
        toast({ 
          title: '❌ Validation Error', 
          description: 'Expected guests cannot be negative!', 
          variant: 'destructive' 
        });
        return;
      }
      
      if (formData.budget && parseFloat(formData.budget) < 0) {
        toast({ 
          title: '❌ Validation Error', 
          description: 'Budget cannot be negative!', 
          variant: 'destructive' 
        });
        return;
      }

      // Date and Time Validation
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Validation: Booking From Date cannot be in the past
      if (formData.bookingDateFrom) {
        const fromDate = new Date(formData.bookingDateFrom);
        if (fromDate < today) {
          toast({ 
            title: '❌ Validation Error', 
            description: 'Booking From Date cannot be in the past!', 
            variant: 'destructive' 
          });
          return;
        }
      }

      // Validation: Booking To Date cannot be in the past
      if (formData.bookingDateTo) {
        const toDate = new Date(formData.bookingDateTo);
        if (toDate < today) {
          toast({ 
            title: '❌ Validation Error', 
            description: 'Booking To Date cannot be in the past!', 
            variant: 'destructive' 
          });
          return;
        }
      }

      // Validation: From Date must be before or equal to To Date
      if (formData.bookingDateFrom && formData.bookingDateTo) {
        const fromDate = new Date(formData.bookingDateFrom);
        const toDate = new Date(formData.bookingDateTo);
        if (fromDate > toDate) {
          toast({ 
            title: '❌ Validation Error', 
            description: 'Booking From Date cannot be after To Date!', 
            variant: 'destructive' 
          });
          return;
        }
      }

      // Validation: If same day, From Time must be before To Time
      if (formData.bookingDateFrom && formData.bookingDateTo && 
          formData.bookingDateFrom === formData.bookingDateTo &&
          formData.bookingTimeFrom && formData.bookingTimeTo) {
        const fromTime = formData.bookingTimeFrom;
        const toTime = formData.bookingTimeTo;
        if (fromTime >= toTime) {
          toast({ 
            title: '❌ Validation Error', 
            description: 'From Time must be before To Time on the same day!', 
            variant: 'destructive' 
          });
          return;
        }
      }

      // Validation: Follow-up date cannot be in the past
      if (formData.followupDate) {
        const followupDate = new Date(formData.followupDate);
        if (followupDate < today) {
          toast({ 
            title: '❌ Validation Error', 
            description: 'Follow-up Date cannot be in the past!', 
            variant: 'destructive' 
          });
          return;
        }
      }

      // Add optional fields only if they have values
      if (formData.email) leadData.email = formData.email;
      // Determine final event type (use custom if "Others" selected)
      if (formData.eventType) {
        const finalEventType = formData.eventType === 'OTHERS' 
          ? formData.customEventType 
          : EVENT_TYPE_OPTIONS.find(o => o.value === formData.eventType)?.label || formData.eventType;
        leadData.eventType = finalEventType;
      }
      if (formData.bookingDateFrom) leadData.bookingDateFrom = formData.bookingDateFrom;
      if (formData.bookingDateTo) leadData.bookingDateTo = formData.bookingDateTo;
      if (formData.bookingTimeFrom) leadData.bookingTimeFrom = formData.bookingTimeFrom;
      if (formData.bookingTimeTo) leadData.bookingTimeTo = formData.bookingTimeTo;
      if (formData.followupDate) leadData.followupDate = formData.followupDate;
      if (formData.expectedGuests) leadData.expectedGuests = parseInt(formData.expectedGuests);
      if (formData.budget) leadData.budget = parseFloat(formData.budget);
      if (formData.status) leadData.status = formData.status;
      if (formData.priority) leadData.priority = formData.priority;
      if (formData.source) leadData.source = formData.source;
      if (formData.notes) leadData.notes = formData.notes;
      if (formData.assignedTo) leadData.assignedTo = formData.assignedTo;


      if (editingLead) {
        await leadsAPI.update(editingLead.id, leadData);
        toast({ title: 'Success', description: 'Lead updated successfully' });
      } else {
        const response = await leadsAPI.create(leadData);
        toast({ title: 'Success', description: 'Lead created successfully' });
      }

      await loadLeads(); // Reload from backend
      resetForm();
      setDialogOpen(false);
    } catch (error) {
      console.error('Failed to save lead - Full error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to save lead. Please try again.';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false); // Re-enable button
    }
  };

  const handleEdit = (lead) => {
    // Block edit when offline
    if (!checkOfflineAction('edit')) return;
    
    setEditingLead(lead);
    // Check if eventType matches predefined options, otherwise set as "OTHERS"
    const matchedOption = EVENT_TYPE_OPTIONS.find(o => o.label === lead.eventType || o.value === lead.eventType);
    setFormData({
      ...lead,
      eventType: matchedOption ? matchedOption.value : 'OTHERS',
      customEventType: matchedOption ? '' : (lead.eventType || '')
    });
    setDialogOpen(true);
  };

  const handleDeleteClick = (lead) => {
    // Block delete when offline
    if (!checkOfflineAction('delete')) return;
    
    setLeadToDelete(lead);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (leadToDelete) {
      try {
        await leadsAPI.delete(leadToDelete.id);
        toast({ title: 'Success', description: 'Lead deleted successfully' });
        await loadLeads(); // Reload from backend
        setDeleteConfirmOpen(false);
        setLeadToDelete(null);
      } catch (error) {
        console.error('Failed to delete lead:', error);
        toast({
          title: 'Error',
          description: error.message || 'Failed to delete lead. Please try again.',
          variant: 'destructive'
        });
      }
    }
  };

  const handleConvertClick = (lead) => {
    setLeadToConvert(lead);
    setBookingType('confirmed');
    setConvertDialogOpen(true);
  };

  const handleConvertToBooking = () => {
    if (leadToConvert) {
      // Prepare booking data from lead
      const bookingData = {
        clientName: leadToConvert.name,
        clientEmail: leadToConvert.email || '',
        clientPhone: leadToConvert.phone,
        eventType: leadToConvert.eventType,
        eventStartDate: leadToConvert.bookingDateFrom || '',
        eventEndDate: leadToConvert.bookingDateTo || '',
        startTime: leadToConvert.bookingTimeFrom || '',
        endTime: leadToConvert.bookingTimeTo || '',
        guestCount: leadToConvert.expectedGuests || '',
        totalAmount: leadToConvert.budget || '',
        bookingType: bookingType,
        venue: '',
        advanceAmount: '',
        requirements: leadToConvert.notes || ''
      };

      // Navigate to bookings page with pre-filled data
      navigate('/bookings', { state: { convertedLead: bookingData, leadId: leadToConvert.id } });
      
      toast({ 
        title: 'Converting Lead', 
        description: `Converting "${leadToConvert.name}" to ${bookingType === 'confirmed' ? 'confirmed booking' : 'blocked date'}` 
      });
    }
  };

  // Handle Update Status Click
  const handleUpdateStatusClick = (lead) => {
    setLeadToUpdate(lead);
    setStatusUpdateData({
      status: lead.status || 'new',
      progressNote: '',
      nextFollowupDate: lead.followupDate ? lead.followupDate.split('T')[0] : ''
    });
    setUpdateStatusOpen(true);
  };

  // Handle Status Update Submit
  const handleStatusUpdateSubmit = async () => {
    if (!leadToUpdate) return;

    try {
      // Get existing notes and append new progress note with timestamp
      const existingNotes = leadToUpdate.notes || '';
      const timestamp = new Date().toLocaleString('en-IN', {
        dateStyle: 'short',
        timeStyle: 'short'
      });
      const newProgressEntry = statusUpdateData.progressNote 
        ? `\n[${timestamp}] Status: ${statusUpdateData.status.toUpperCase()} - ${statusUpdateData.progressNote}`
        : '';
      const updatedNotes = existingNotes + newProgressEntry;

      // Update the lead with new status, notes, and follow-up date
      const updatePayload = {
        ...leadToUpdate,
        status: statusUpdateData.status,
        notes: updatedNotes.trim()
      };
      
      // Add follow-up date if provided
      if (statusUpdateData.nextFollowupDate) {
        updatePayload.followupDate = statusUpdateData.nextFollowupDate;
      }

      await leadsAPI.update(leadToUpdate.id, updatePayload);

      const followupMsg = statusUpdateData.nextFollowupDate 
        ? ` Next follow-up: ${new Date(statusUpdateData.nextFollowupDate).toLocaleDateString('en-IN')}`
        : '';
      
      toast({ 
        title: 'Status Updated', 
        description: `Lead status changed to ${statusUpdateData.status.replace('_', ' ').toUpperCase()}.${followupMsg}` 
      });
      
      await loadLeads(); // Reload from backend
      setUpdateStatusOpen(false);
      setLeadToUpdate(null);
      setStatusUpdateData({ status: '', progressNote: '', nextFollowupDate: '' });
    } catch (error) {
      console.error('Failed to update lead status:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update status. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      eventType: 'WEDDING',
      customEventType: '',
      bookingDateFrom: '',
      bookingDateTo: '',
      bookingTimeFrom: '',
      bookingTimeTo: '',
      followupDate: '',
      expectedGuests: '',
      budget: '',
      status: 'new',
      priority: 'warm',
      source: '',
      notes: '',
      assignedTo: ''
    });
    setEditingLead(null);
    setFieldErrors({}); // Clear validation errors
  };

  // Follow-up lead handlers
  const handleFollowUpLeadClick = (lead) => {
    setSelectedFollowUpLead(lead);
    setCallDialogOpen(true);
  };

  const handleCallConfirm = () => {
    if (selectedFollowUpLead) {
      window.location.href = `tel:${selectedFollowUpLead.phone}`;
      setCallDialogOpen(false);
    }
  };

  const handleOpenStatusUpdate = () => {
    setCallDialogOpen(false);
    setNewStatusForFollowUp(selectedFollowUpLead?.status || 'contacted');
    setFollowUpProgressNote('');
    setStatusUpdateDialogOpen(true);
  };

  const handleFollowUpStatusUpdate = async () => {
    if (!selectedFollowUpLead) return;
    
    try {
      const existingNotes = selectedFollowUpLead.notes || '';
      const timestamp = new Date().toLocaleString('en-IN', {
        dateStyle: 'short',
        timeStyle: 'short'
      });
      const newProgressEntry = followUpProgressNote 
        ? `\n[${timestamp}] Status: ${newStatusForFollowUp.toUpperCase()} - ${followUpProgressNote}`
        : `\n[${timestamp}] Status changed to: ${newStatusForFollowUp.toUpperCase()}`;
      
      await leadsAPI.update(selectedFollowUpLead.id, {
        ...selectedFollowUpLead,
        status: newStatusForFollowUp,
        notes: (existingNotes + newProgressEntry).trim()
      });
      
      toast({ 
        title: 'Status Updated', 
        description: `Lead status changed to ${newStatusForFollowUp.replace('_', ' ').toUpperCase()}` 
      });
      
      await loadLeads();
      setStatusUpdateDialogOpen(false);
      setSelectedFollowUpLead(null);
      setFollowUpProgressNote('');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive'
      });
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      new: { color: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700', icon: UserPlus, iconColor: 'text-blue-600 dark:text-blue-400', label: 'New' },
      contacted: { color: 'bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-700', icon: PhoneCall, iconColor: 'text-purple-600 dark:text-purple-400', label: 'Contacted' },
      in_followup: { color: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700', icon: Clock, iconColor: 'text-yellow-600 dark:text-yellow-400', label: 'In Followup' },
      won: { color: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 border-green-200 dark:border-green-700', icon: CheckCircle, iconColor: 'text-green-600 dark:text-green-400', label: 'Won' },
      lost: { color: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700', icon: XCircle, iconColor: 'text-red-600 dark:text-red-400', label: 'Lost' },
      // Legacy statuses for backward compatibility
      hot: { color: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700', icon: Flame, iconColor: 'text-red-600 dark:text-red-400', label: 'Hot' },
      warm: { color: 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300 border-orange-200 dark:border-orange-700', icon: Flame, iconColor: 'text-orange-600 dark:text-orange-400', label: 'Warm' },
      cold: { color: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700', icon: Flame, iconColor: 'text-blue-600 dark:text-blue-400', label: 'Cold' }
    };
    return configs[status] || configs.new;
  };

  const getPriorityConfig = (priority) => {
    const configs = {
      hot: { color: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 border-red-200 dark:border-red-700', icon: Flame, iconColor: 'text-red-600 dark:text-red-400', label: 'Hot' },
      warm: { color: 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300 border-orange-200 dark:border-orange-700', icon: Flame, iconColor: 'text-orange-600 dark:text-orange-400', label: 'Warm' },
      cold: { color: 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-700', icon: Flame, iconColor: 'text-blue-600 dark:text-blue-400', label: 'Cold' }
    };
    return configs[priority] || configs.warm;
  };

  // Format role for display (capitalize first letter)
  const formatRole = (role) => {
    if (!role) return '';
    return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
  };

  // Download Report Functions for Leads
  // Helper function to convert 24-hour time to 12-hour AM/PM format
  const formatTimeToAMPM = (time24) => {
    if (!time24 || time24 === '-') return '-';
    try {
      const [hours, minutes] = time24.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12;
      return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch (e) {
      return time24;
    }
  };

  const prepareLeadsReportData = () => {
    return filteredLeads.map((lead, index) => ({
      'S.No': index + 1,
      'Name': lead.name || '-',
      'Contact': lead.phone || '-',
      'Email': lead.email || '-',
      'Event Type': lead.eventType || '-',
      'Event Date': lead.bookingDateFrom ? formatDate(lead.bookingDateFrom) : '-',
      'Event End Date': lead.bookingDateTo ? formatDate(lead.bookingDateTo) : '-',
      'From Time': formatTimeToAMPM(lead.bookingTimeFrom),
      'To Time': formatTimeToAMPM(lead.bookingTimeTo),
      'Expected Guests': lead.expectedGuests || '-',
      'Budget': Number(lead.budget) || 0,
      'Source': lead.source || '-',
      'Status': (lead.status || 'new').replace('_', ' ').toUpperCase(),
      'Priority': (lead.priority || 'warm').toUpperCase(),
      'Follow-up Date': lead.followupDate ? formatDate(lead.followupDate) : 'Not Set',
      'Notes': lead.notes || '-',
      'Created Date': lead.createdAt ? formatDate(lead.createdAt) : '-'
    }));
  };

  const downloadLeadsExcel = () => {
    try {
      const data = prepareLeadsReportData();
      const worksheet = XLSX.utils.json_to_sheet(data);
      
      // Set column widths
      worksheet['!cols'] = [
        { wch: 5 },   // S.No
        { wch: 20 },  // Name
        { wch: 15 },  // Contact
        { wch: 25 },  // Email
        { wch: 15 },  // Event Type
        { wch: 12 },  // Event Date
        { wch: 12 },  // Event End Date
        { wch: 10 },  // From Time
        { wch: 10 },  // To Time
        { wch: 12 },  // Expected Guests
        { wch: 15 },  // Budget
        { wch: 15 },  // Source
        { wch: 12 },  // Status
        { wch: 10 },  // Priority
        { wch: 15 },  // Follow-up Date
        { wch: 30 },  // Notes
        { wch: 12 },  // Created Date
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Leads Report');
      
      const fileName = `Leads_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
      toast({
        title: 'Download Started',
        description: `Excel report "${fileName}" is being downloaded.`
      });
    } catch (error) {
      console.error('Excel download error:', error);
      toast({
        title: 'Download Failed',
        description: 'Failed to generate Excel report. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const downloadLeadsPDF = () => {
    try {
      const data = prepareLeadsReportData();
      const doc = new jsPDF('landscape', 'mm', 'a4');
      
      // Helper function to format currency for PDF (without rupee symbol)
      const formatCurrencyForPDF = (amount) => {
        return 'Rs. ' + Number(amount || 0).toLocaleString('en-IN');
      };
      
      // Title
      doc.setFontSize(18);
      doc.setTextColor(249, 115, 22); // Orange color
      doc.text('Leads Report', 14, 15);
      
      // Subtitle with date
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text('Generated on: ' + new Date().toLocaleString('en-IN'), 14, 22);
      doc.text('Total Records: ' + data.length, 14, 27);

      // Table
      const tableColumn = ['S.No', 'Name', 'Contact', 'Email', 'Event Type', 'Event Date', 'Guests', 'Budget', 'Status', 'Priority', 'Follow-up'];
      const tableRows = data.map(item => [
        item['S.No'],
        item['Name'],
        item['Contact'],
        item['Email'],
        item['Event Type'],
        item['Event Date'],
        item['Expected Guests'],
        item['Budget'] > 0 ? formatCurrencyForPDF(item['Budget']) : '-',
        item['Status'],
        item['Priority'],
        item['Follow-up Date']
      ]);

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 32,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 2,
        },
        headStyles: {
          fillColor: [249, 115, 22],
          textColor: 255,
          fontStyle: 'bold',
        },
        alternateRowStyles: {
          fillColor: [255, 247, 237],
        },
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 25 },
          2: { cellWidth: 22 },
          3: { cellWidth: 35 },
          4: { cellWidth: 20 },
          5: { cellWidth: 22 },
          6: { cellWidth: 15 },
          7: { cellWidth: 22 },
          8: { cellWidth: 22 },
          9: { cellWidth: 18 },
          10: { cellWidth: 22 },
        }
      });

      // Summary at the bottom
      const finalY = (doc.lastAutoTable?.finalY || 150) + 10;
      const statusCounts = {
        new: data.filter(l => l['Status'] === 'NEW').length,
        contacted: data.filter(l => l['Status'] === 'CONTACTED').length,
        in_followup: data.filter(l => l['Status'] === 'IN FOLLOWUP' || l['Status'] === 'IN_FOLLOWUP').length,
        won: data.filter(l => l['Status'] === 'WON').length,
        lost: data.filter(l => l['Status'] === 'LOST').length,
      };
      const totalBudget = data.reduce((sum, item) => sum + item['Budget'], 0);
      
      doc.setFontSize(10);
      doc.setTextColor(0);
      doc.text('Summary:', 14, finalY);
      doc.text('New: ' + statusCounts.new + ' | Contacted: ' + statusCounts.contacted + ' | In Follow-up: ' + statusCounts.in_followup + ' | Won: ' + statusCounts.won + ' | Lost: ' + statusCounts.lost, 14, finalY + 6);
      doc.text('Total Expected Budget: ' + formatCurrencyForPDF(totalBudget), 14, finalY + 12);

      const fileName = 'Leads_Report_' + new Date().toISOString().split('T')[0] + '.pdf';
      doc.save(fileName);
      
      toast({
        title: 'Download Started',
        description: 'PDF report "' + fileName + '" is being downloaded.'
      });
    } catch (error) {
      console.error('PDF download error:', error);
      toast({
        title: 'Download Failed',
        description: 'Failed to generate PDF report. Please try again.',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate('/dashboard')}
        className="flex items-center text-gray-600 hover:text-gray-900 -ml-2 h-8 sm:h-10 text-xs sm:text-sm"
      >
        <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
        Back to Dashboard
      </Button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-foreground">Lead Management</h1>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-muted-foreground mt-1">Track and manage potential customers</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          {canCreateLeads() && (
            <DialogTrigger asChild>
              <Button 
                className="bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 shadow-md h-9 sm:h-10 text-sm"
                onClick={(e) => {
                  if (!checkOfflineAction('create')) {
                    e.preventDefault();
                  }
                }}
                disabled={isOffline}
              >
                <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                Add Lead
              </Button>
            </DialogTrigger>
          )}
          <DialogContent className="w-full sm:w-[95vw] max-w-2xl max-h-[90vh] sm:max-h-[85vh] overflow-y-auto p-4 sm:p-6">
            <DialogHeader className="pb-2 sm:pb-4">
              <DialogTitle className="text-base sm:text-xl">{editingLead ? 'Edit Lead' : 'Add New Lead'}</DialogTitle>
              <DialogDescription className="text-xs sm:text-sm">
                {editingLead ? 'Update lead information' : 'Capture potential customer details'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-6 mt-2 sm:mt-4 w-full">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1.5 sm:space-y-2">
                  <Label htmlFor="name" className={`text-xs sm:text-sm ${fieldErrors.name ? 'text-red-600' : ''}`}>Name *</Label>
                  <Input 
                    id="name" 
                    value={formData.name} 
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    onBlur={() => handleFieldBlur('name')}
                    required 
                    className={`h-9 sm:h-10 text-sm bg-background dark:bg-background ${fieldErrors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500 bg-red-50 dark:bg-red-950' : ''}`}
                  />
                  {fieldErrors.name && (
                    <p className="text-[10px] sm:text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {fieldErrors.name}
                    </p>
                  )}
                </div>
                <div className="space-y-1.5 sm:space-y-2">
                  <Label htmlFor="phone" className={`text-xs sm:text-sm ${fieldErrors.phone ? 'text-red-600' : ''}`}>Phone * (10 digits)</Label>
                  <Input 
                    id="phone" 
                    type="tel" 
                    placeholder="9876543210"
                    value={formData.phone} 
                    onChange={(e) => {
                      const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 10);
                      handleFieldChange('phone', digitsOnly);
                    }}
                    onBlur={() => handleFieldBlur('phone')}
                    maxLength="10"
                    required
                    className={`h-9 sm:h-10 text-sm bg-background dark:bg-background ${fieldErrors.phone ? 'border-red-500 focus:border-red-500 focus:ring-red-500 bg-red-50 dark:bg-red-950' : ''}`}
                  />
                  {fieldErrors.phone && (
                    <p className="text-[10px] sm:text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {fieldErrors.phone}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className={fieldErrors.email ? 'text-red-600' : ''}>Email</Label>
                <Input 
                  id="email" 
                  type="email" 
                  value={formData.email} 
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                  onBlur={() => handleFieldBlur('email')}
                  placeholder="Optional" 
                  className={`bg-background dark:bg-background ${fieldErrors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500 bg-red-50 dark:bg-red-950' : ''}`}
                />
                {fieldErrors.email && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> {fieldErrors.email}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="eventType" className="text-sm sm:text-base">Event Type</Label>
                  <Select 
                    value={formData.eventType} 
                    onValueChange={(value) => setFormData({...formData, eventType: value, customEventType: value === 'OTHERS' ? formData.customEventType : ''})}
                  >
                    <SelectTrigger className="h-11 sm:h-10">
                      <SelectValue placeholder="Select event type" />
                    </SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPE_OPTIONS.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expectedGuests" className="text-sm sm:text-base">Expected Guests</Label>
                  <Input 
                    id="expectedGuests" 
                    type="number" 
                    min="0"
                    value={formData.expectedGuests} 
                    onChange={(e) => setFormData({...formData, expectedGuests: e.target.value})} 
                    placeholder="Optional"
                    className={`h-11 sm:h-10 text-base sm:text-sm ${formData.expectedGuests && parseInt(formData.expectedGuests) < 0 ? 'border-red-500' : ''}`}
                  />
                  {formData.expectedGuests && parseInt(formData.expectedGuests) < 0 && (
                    <p className="text-xs text-red-500 mt-1">⚠️ Cannot be negative</p>
                  )}
                </div>
              </div>

              {/* Custom Event Type - shows when "Others" is selected */}
              {formData.eventType === 'OTHERS' && (
                <div className="space-y-2">
                  <Label htmlFor="customEventType" className="text-sm sm:text-base">Specify Event Type *</Label>
                  <Input 
                    id="customEventType" 
                    value={formData.customEventType} 
                    onChange={(e) => setFormData({...formData, customEventType: e.target.value})} 
                    placeholder="Enter event type"
                    required 
                    className="h-11 sm:h-10"
                  />
                </div>
              )}

              {/* Booking Date Range */}
              <div className="space-y-3">
                <Label className="text-sm sm:text-base font-semibold">Booking Date & Time Range</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bookingDateFrom" className="text-xs sm:text-sm md:text-base">From Date</Label>
                    <Input 
                      type="date"
                      value={formData.bookingDateFrom} 
                      onChange={(e) => setFormData({...formData, bookingDateFrom: e.target.value})}
                      min={new Date().toISOString().split('T')[0]}
                      className="h-[44px] sm:h-11 md:h-10 text-[16px] sm:text-sm md:text-base w-full rounded-md border-gray-300 focus:border-teal-500 focus:ring-teal-500 appearance-none px-3"
                    />
                    {formData.bookingDateFrom && new Date(formData.bookingDateFrom) < new Date(new Date().toDateString()) && (
                      <p className="text-xs text-red-500">⚠️ Cannot be in the past</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bookingTimeFrom" className="text-xs sm:text-sm md:text-base">From Time</Label>
                    <TimePicker 
                      id="bookingTimeFrom" 
                      value={formData.bookingTimeFrom} 
                      onChange={(time) => setFormData({...formData, bookingTimeFrom: time})} 
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bookingDateTo" className="text-xs sm:text-sm md:text-base">To Date</Label>
                    <Input 
                      type="date"
                      value={formData.bookingDateTo} 
                      onChange={(e) => setFormData({...formData, bookingDateTo: e.target.value})}
                      min={formData.bookingDateFrom || new Date().toISOString().split('T')[0]}
                      className="h-[44px] sm:h-11 md:h-10 text-[16px] sm:text-sm md:text-base w-full rounded-md border-gray-300 focus:border-teal-500 focus:ring-teal-500 appearance-none px-3"
                    />
                    {formData.bookingDateTo && new Date(formData.bookingDateTo) < new Date(new Date().toDateString()) && (
                      <p className="text-xs text-red-500">⚠️ Cannot be in the past</p>
                    )}
                    {formData.bookingDateFrom && formData.bookingDateTo && new Date(formData.bookingDateTo) < new Date(formData.bookingDateFrom) && (
                      <p className="text-xs text-red-500">⚠️ Must be after From Date</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="bookingTimeTo" className="text-xs sm:text-sm md:text-base">To Time</Label>
                    <TimePicker 
                      id="bookingTimeTo" 
                      value={formData.bookingTimeTo} 
                      onChange={(time) => setFormData({...formData, bookingTimeTo: time})}
                    />
                    {formData.bookingDateFrom === formData.bookingDateTo && 
                     formData.bookingTimeFrom && formData.bookingTimeTo && 
                     formData.bookingTimeFrom >= formData.bookingTimeTo && (
                      <p className="text-xs text-red-500">⚠️ Must be after From Time</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="followupDate" className="text-xs sm:text-sm md:text-base">Follow-up Date</Label>
                  <Input 
                    type="date"
                    value={formData.followupDate} 
                    onChange={(e) => setFormData({...formData, followupDate: e.target.value})}
                    min={new Date().toISOString().split('T')[0]}
                    className="h-[44px] sm:h-11 md:h-10 text-[16px] sm:text-sm md:text-base w-full rounded-md border-gray-300 focus:border-teal-500 focus:ring-teal-500 appearance-none px-3"
                  />
                  {formData.followupDate && new Date(formData.followupDate) < new Date(new Date().toDateString()) && (
                    <p className="text-xs text-red-500">⚠️ Cannot be in the past</p>
                  )}
                </div>
                {!hideFinancials && (
                  <div className="space-y-2">
                    <Label htmlFor="budget">Budget (₹)</Label>
                    <Input 
                      id="budget" 
                      type="number" 
                      min="0"
                      value={formData.budget} 
                      onChange={(e) => setFormData({...formData, budget: e.target.value})} 
                      placeholder="Optional"
                      className={formData.budget && parseFloat(formData.budget) < 0 ? 'border-red-500' : ''}
                    />
                    {formData.budget && parseFloat(formData.budget) < 0 && (
                      <p className="text-xs text-red-500 mt-1">⚠️ Cannot be negative</p>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="status">Lead Status</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="in_followup">In Followup</SelectItem>
                      <SelectItem value="won">Won</SelectItem>
                      <SelectItem value="lost">Lost</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="priority">Priority</Label>
                  <Select value={formData.priority} onValueChange={(value) => setFormData({...formData, priority: value})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hot">🔥 Hot - Urgent</SelectItem>
                      <SelectItem value="warm">🌡️ Warm - Moderate</SelectItem>
                      <SelectItem value="cold">❄️ Cold - Low Priority</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="source" className="text-sm sm:text-base">Source</Label>
                  <Input id="source" value={formData.source} onChange={(e) => setFormData({...formData, source: e.target.value})} placeholder="e.g., Website, Referral" className="h-11 sm:h-10 text-base sm:text-sm" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="assignedTo" className="text-sm sm:text-base">Assigned To</Label>
                  <Input id="assignedTo" value={formData.assignedTo} onChange={(e) => setFormData({...formData, assignedTo: e.target.value})} className="h-11 sm:h-10 text-base sm:text-sm" />
                </div>
              </div>

              {/* Submit Buttons - Sticky on mobile */}
              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-4 sm:pt-6 sticky bottom-0 bg-white dark:bg-card pb-2 sm:pb-0 -mx-4 sm:mx-0 px-4 sm:px-0 border-t dark:border-border sm:border-t-0 mt-4 sm:mt-0">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => { setDialogOpen(false); resetForm(); }} 
                  className="h-11 sm:h-10 order-2 sm:order-1"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="h-11 sm:h-10 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 order-1 sm:order-2"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {editingLead ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>{editingLead ? 'Update' : 'Create'} Lead</>
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* ROW 1: Total Leads, New Leads, Month Wise Leads, Hot Leads */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-xs font-medium text-gray-600 dark:text-muted-foreground truncate">Total Leads</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-foreground mt-0.5 sm:mt-1">{leads.length}</p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 dark:bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-muted-foreground" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20" 
          onClick={() => setFollowUpModalOpen(true)}
          data-testid="followup-this-month-card"
        >
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-xs font-medium text-orange-700 dark:text-orange-400 truncate">Follow-ups This Month</p>
                <p className="text-lg sm:text-2xl font-bold text-orange-600 dark:text-orange-400 mt-0.5 sm:mt-1">{followUpLeadsThisMonth.length}</p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20" 
          onClick={() => navigate('/monthly-leads')}
        >
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-xs font-medium text-teal-700 dark:text-teal-400 truncate">Month Wise</p>
                <p className="text-[10px] sm:text-xs text-teal-600 dark:text-teal-400 mt-0.5 flex items-center gap-1">
                  View <ArrowRight className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                </p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setPriorityFilter('hot')}>
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-xs font-medium text-gray-600 dark:text-muted-foreground truncate">🔥 Hot Leads</p>
                <p className="text-lg sm:text-2xl font-bold text-red-600 mt-0.5 sm:mt-1">{leads.filter(l => l.priority === 'hot').length}</p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <Flame className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ROW 2: Contacted, In Followup, Won, Lost */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        <Card className="border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setStatusFilter('contacted')}>
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-xs font-medium text-gray-600 dark:text-muted-foreground truncate">Contacted</p>
                <p className="text-lg sm:text-2xl font-bold text-purple-600 mt-0.5 sm:mt-1">{leads.filter(l => l.status === 'contacted').length}</p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <PhoneCall className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setStatusFilter('in_followup')}>
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-xs font-medium text-gray-600 dark:text-muted-foreground truncate">In Followup</p>
                <p className="text-lg sm:text-2xl font-bold text-yellow-600 dark:text-yellow-500 mt-0.5 sm:mt-1">{leads.filter(l => l.status === 'in_followup').length}</p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 dark:text-yellow-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setStatusFilter('won')}>
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-xs font-medium text-gray-600 dark:text-muted-foreground truncate">Won</p>
                <p className="text-lg sm:text-2xl font-bold text-green-600 mt-0.5 sm:mt-1">{leads.filter(l => l.status === 'won').length}</p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setStatusFilter('lost')}>
          <CardContent className="p-2.5 sm:p-4">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] sm:text-xs font-medium text-gray-600 dark:text-muted-foreground truncate">Lost</p>
                <p className="text-lg sm:text-2xl font-bold text-red-600 mt-0.5 sm:mt-1">{leads.filter(l => l.status === 'lost').length}</p>
              </div>
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-3 sm:p-4 lg:p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by name, email, or lead ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="in_followup">In Followup</SelectItem>
                <SelectItem value="won">Won</SelectItem>
                <SelectItem value="lost">Lost</SelectItem>
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Filter by priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="hot">🔥 Hot</SelectItem>
                <SelectItem value="warm">🌡️ Warm</SelectItem>
                <SelectItem value="cold">❄️ Cold</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Download Report Buttons */}
          {canExportLeads() && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 mt-4 pt-4 border-t dark:border-border">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-muted-foreground">
                <Download className="w-4 h-4" />
                <span className="font-medium">Download Report:</span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadLeadsExcel}
                  className="flex-1 sm:flex-none hover:bg-green-50 dark:hover:bg-green-900/20 hover:border-green-300 hover:text-green-700 dark:hover:text-green-400"
                  disabled={filteredLeads.length === 0}
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
                  Excel
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadLeadsPDF}
                  className="flex-1 sm:flex-none hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-300 hover:text-red-700 dark:hover:text-red-400"
                  disabled={filteredLeads.length === 0}
                >
                  <FileText className="w-4 h-4 mr-2 text-red-600" />
                  PDF
                </Button>
              </div>
              <span className="text-xs text-gray-500 dark:text-muted-foreground">
                ({filteredLeads.length} records)
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Leads Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredLeads.length > 0 ? (
          filteredLeads.map((lead) => {
            const statusConfig = getStatusConfig(lead.status);
            const StatusIcon = statusConfig.icon;
            const priorityConfig = getPriorityConfig(lead.priority || 'warm');
            const PriorityIcon = priorityConfig.icon;
            return (
              <Card key={lead.id} className="border-0 shadow-md hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center flex-wrap gap-2">
                          <h3 className="text-lg font-bold text-gray-900 dark:text-foreground">{lead.name}</h3>
                          <Badge className={statusConfig.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                          <Badge className={priorityConfig.color}>
                            <PriorityIcon className="w-3 h-3 mr-1" />
                            {priorityConfig.label}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-muted-foreground mt-1">Lead ID: {lead.id}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                      <div className="flex items-center text-sm text-gray-700 dark:text-foreground">
                        <Phone className="w-4 h-4 mr-2 text-gray-400 dark:text-muted-foreground" />
                        {lead.phone}
                      </div>
                      <div className="flex items-center text-sm text-gray-700 dark:text-foreground">
                        <Mail className="w-4 h-4 mr-2 text-gray-400 dark:text-muted-foreground" />
                        {lead.email}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 pt-3 border-t dark:border-border">
                      <div>
                        <p className="text-xs text-gray-500 dark:text-muted-foreground">Event Type</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-foreground mt-1">{lead.eventType}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-muted-foreground">Booking Date Range</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-foreground mt-1">
                          {lead.bookingDateFrom && lead.bookingDateTo 
                            ? `${formatDate(lead.bookingDateFrom)} - ${formatDate(lead.bookingDateTo)}`
                            : 'TBD'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-muted-foreground">Follow-up Date</p>
                        <p className="text-sm font-medium text-orange-600 dark:text-orange-400 mt-1">
                          {lead.followupDate ? formatDate(lead.followupDate) : 'Not set'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 dark:text-muted-foreground">Guests</p>
                        <p className="text-sm font-medium text-gray-900 dark:text-foreground mt-1">{lead.expectedGuests || 'N/A'}</p>
                      </div>
                    </div>
                    
                    {!hideFinancials && lead.budget && (
                      <div className="bg-teal-50 dark:bg-teal-900/30 p-3 rounded-lg border border-teal-100 dark:border-teal-800">
                        <p className="text-xs text-teal-700 dark:text-teal-300 uppercase font-medium">💰 Budget</p>
                        <p className="text-lg font-bold text-teal-600 dark:text-teal-400 mt-1">{formatCurrency(lead.budget)}</p>
                      </div>
                    )}

                    {lead.notes && (
                      <div className="text-sm text-gray-600 dark:text-muted-foreground bg-gray-50 dark:bg-secondary p-3 rounded-lg">
                        <span className="font-medium">Notes:</span> {lead.notes}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-3 border-t dark:border-border">
                      <div className="text-xs text-gray-500 dark:text-muted-foreground space-y-1">
                        <p>Source: <span className="text-gray-700 dark:text-foreground font-medium">{lead.source}</span></p>
                        <p>Assigned: <span className="text-gray-700 dark:text-foreground font-medium">{lead.assignedTo}</span></p>
                        {/* Lead Created Date */}
                        {lead.createdAt && (
                          <p className="text-gray-500 dark:text-muted-foreground">
                            <span className="font-medium">Lead Created:</span>{' '}
                            <span className="text-gray-600 dark:text-muted-foreground">{formatDateTime(lead.createdAt)}</span>
                          </p>
                        )}
                        {/* Creator and Editor Info */}
                        {lead.createdByName && (
                          <div className="flex items-center flex-wrap gap-1">
                            <span className="font-medium">Created by:</span>
                            <span className="text-gray-700 dark:text-foreground">{lead.createdByName}</span>
                            {lead.createdByRole && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                                {formatRole(lead.createdByRole)}
                              </Badge>
                            )}
                            {lead.createdAt && (
                              <span className="text-[10px] text-gray-400 dark:text-muted-foreground ml-1">
                                ({formatDateTime(lead.createdAt)})
                              </span>
                            )}
                          </div>
                        )}
                        {lead.updatedByName && (
                          <div className="flex items-center flex-wrap gap-1">
                            <span className="font-medium">Last edited by:</span>
                            <span className="text-gray-700 dark:text-foreground">{lead.updatedByName}</span>
                            {lead.updatedByRole && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                                {formatRole(lead.updatedByRole)}
                              </Badge>
                            )}
                            {lead.updatedAt && (
                              <span className="text-[10px] text-gray-400 dark:text-muted-foreground ml-1">
                                ({formatDateTime(lead.updatedAt)})
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          onClick={() => handleConvertClick(lead)}
                          size="sm"
                          className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 w-full"
                        >
                          <ArrowRight className="w-4 h-4 mr-1" />
                          Convert to Booking
                        </Button>
                        <div className="flex space-x-2">
                          <Button
                            onClick={() => handleUpdateStatusClick(lead)}
                            variant="outline"
                            size="sm"
                            className="flex-1 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-700 hover:text-blue-700 dark:hover:text-blue-400 transition-colors"
                            title="Update Status"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                          {canEditLeads() && (
                            <Button
                              onClick={() => handleEdit(lead)}
                              variant="outline"
                              size="sm"
                              className="flex-1 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:border-orange-300 dark:hover:border-orange-700 hover:text-orange-700 dark:hover:text-orange-400 transition-colors"
                              title="Edit Lead"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                          {canDeleteLeads() && (
                            <Button
                              onClick={() => handleDeleteClick(lead)}
                              variant="outline"
                              size="sm"
                              className="flex-1 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-300 dark:hover:border-red-700 hover:text-red-700 dark:hover:text-red-400 transition-colors"
                              title="Delete Lead"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Card className="border-0 shadow-md col-span-2">
            <CardContent className="p-12 text-center">
              <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">No leads found</p>
              <p className="text-gray-500 text-sm mt-2">Try adjusting your filters or add a new lead</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Convert to Booking Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Convert Lead to Booking</DialogTitle>
            <DialogDescription>
              Convert &quot;{leadToConvert?.name}&quot; into a booking. All lead details will be transferred automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {leadToConvert && (
              <div className="p-4 bg-gray-50 dark:bg-secondary rounded-lg border border-gray-200 dark:border-border space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-muted-foreground">Name:</span>
                  <span className="text-sm font-medium dark:text-foreground">{leadToConvert.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-muted-foreground">Phone:</span>
                  <span className="text-sm font-medium dark:text-foreground">{leadToConvert.phone}</span>
                </div>
                {leadToConvert.email && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-muted-foreground">Email:</span>
                    <span className="text-sm font-medium dark:text-foreground">{leadToConvert.email}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600 dark:text-muted-foreground">Event Type:</span>
                  <span className="text-sm font-medium dark:text-foreground">{leadToConvert.eventType}</span>
                </div>
                {leadToConvert.bookingDateFrom && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600 dark:text-muted-foreground">Date Range:</span>
                    <span className="text-sm font-medium dark:text-foreground">
                      {formatDate(leadToConvert.bookingDateFrom)} - {formatDate(leadToConvert.bookingDateTo)}
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="bookingType">Booking Type *</Label>
              <Select value={bookingType} onValueChange={setBookingType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmed">✅ Confirmed Booking</SelectItem>
                  <SelectItem value="blocked">🚫 Block Date</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 dark:text-muted-foreground mt-1">
                Choose whether this is a confirmed booking or just blocking the date.
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConvertDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConvertToBooking}
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              Convert & Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={updateStatusOpen} onOpenChange={setUpdateStatusOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-blue-600" />
              Update Lead Status
            </DialogTitle>
            <DialogDescription>
              Update the status and add progress notes for {leadToUpdate?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {/* Current Status Display */}
            {leadToUpdate && (
              <div className="bg-gray-50 dark:bg-secondary p-3 rounded-lg">
                <p className="text-xs text-gray-500 dark:text-muted-foreground mb-1">Current Status</p>
                <Badge className={getStatusConfig(leadToUpdate.status).color}>
                  {(() => {
                    const StatusIcon = getStatusConfig(leadToUpdate.status).icon;
                    return <StatusIcon className={`w-3 h-3 ${getStatusConfig(leadToUpdate.status).iconColor}`} />;
                  })()}
                  <span className="ml-1">{leadToUpdate.status?.replace('_', ' ').toUpperCase()}</span>
                </Badge>
              </div>
            )}

            {/* New Status Selection */}
            <div className="space-y-2">
              <Label htmlFor="newStatus">New Status</Label>
              <Select 
                value={statusUpdateData.status} 
                onValueChange={(value) => setStatusUpdateData(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select new status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">
                    <div className="flex items-center gap-2">
                      <UserPlus className="w-4 h-4 text-blue-500" />
                      <span>New</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="contacted">
                    <div className="flex items-center gap-2">
                      <PhoneCall className="w-4 h-4 text-purple-500" />
                      <span>Contacted</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="in_followup">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-yellow-500" />
                      <span>In Follow-up</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="won">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span>Won</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="lost">
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span>Lost</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Progress Note */}
            <div className="space-y-2">
              <Label htmlFor="progressNote">Progress Note</Label>
              <Textarea
                id="progressNote"
                placeholder="What progress has been made? (e.g., Called client, discussed rates, scheduled visit...)"
                value={statusUpdateData.progressNote}
                onChange={(e) => setStatusUpdateData(prev => ({ ...prev, progressNote: e.target.value }))}
                rows={3}
                className="resize-none"
              />
              <p className="text-xs text-gray-500 dark:text-muted-foreground">This note will be appended to the lead&apos;s history with timestamp</p>
            </div>

            {/* Next Follow-up Date */}
            <div className="space-y-2">
              <Label htmlFor="nextFollowupDate" className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-500" />
                Next Follow-up Date
              </Label>
              <Input
                id="nextFollowupDate"
                type="date"
                value={statusUpdateData.nextFollowupDate}
                onChange={(e) => setStatusUpdateData(prev => ({ ...prev, nextFollowupDate: e.target.value }))}
                min={new Date().toISOString().split('T')[0]}
                className="h-[44px] sm:h-11 md:h-10 text-[16px] sm:text-sm md:text-base w-full rounded-md border-gray-300 dark:border-border focus:border-teal-500 focus:ring-teal-500 appearance-none px-3"
              />
              {leadToUpdate?.followupDate && (
                <p className="text-xs text-gray-500 dark:text-muted-foreground">
                  Current follow-up: {new Date(leadToUpdate.followupDate).toLocaleDateString('en-IN', { dateStyle: 'medium' })}
                </p>
              )}
            </div>

            {/* Previous Notes Preview */}
            {leadToUpdate?.notes && (
              <div className="space-y-2">
                <Label className="text-gray-600 dark:text-muted-foreground">Previous Notes</Label>
                <div className="bg-gray-50 dark:bg-secondary p-3 rounded-lg max-h-32 overflow-y-auto">
                  <p className="text-sm text-gray-700 dark:text-foreground whitespace-pre-wrap">{leadToUpdate.notes}</p>
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setUpdateStatusOpen(false);
                setLeadToUpdate(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleStatusUpdateSubmit}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={!statusUpdateData.status}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Update Status
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        onConfirm={handleDeleteConfirm}
        title="Delete Lead?"
        description="This will permanently delete this lead from the system. This action cannot be undone."
        itemName={leadToDelete ? `${leadToDelete.name} - ${leadToDelete.eventType}` : ''}
        confirmText="Delete Lead"
      />

      {/* Follow-ups This Month Modal */}
      <Dialog open={followUpModalOpen} onOpenChange={setFollowUpModalOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-orange-600" />
              Follow-ups This Month
            </DialogTitle>
            <DialogDescription>
              {followUpLeadsThisMonth.length} lead{followUpLeadsThisMonth.length !== 1 ? 's' : ''} to follow up this month
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-2 py-4">
            {followUpLeadsThisMonth.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-muted-foreground">
                <Phone className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No follow-ups scheduled for this month</p>
              </div>
            ) : (
              followUpLeadsThisMonth.map((lead) => {
                const isOverdue = new Date(lead.followupDate) < new Date();
                return (
                  <div
                    key={lead.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                      isOverdue ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' : 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800'
                    }`}
                    onClick={() => handleFollowUpLeadClick(lead)}
                    data-testid={`followup-lead-${lead.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-900 dark:text-foreground truncate">{lead.name}</p>
                          {isOverdue && (
                            <Badge className="bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400 text-[10px]">Overdue</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-muted-foreground">{lead.phone}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={getStatusConfig(lead.status).color + ' text-[10px]'}>
                            {getStatusConfig(lead.status).label}
                          </Badge>
                          <span className="text-xs text-gray-500 dark:text-muted-foreground">
                            {formatDate(lead.followupDate)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                          <Phone className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Call Lead Confirmation Dialog */}
      <Dialog open={callDialogOpen} onOpenChange={setCallDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Phone className="w-5 h-5 text-orange-600" />
              <span>Call Lead?</span>
            </DialogTitle>
            <DialogDescription>
              Choose an action for this follow-up lead
            </DialogDescription>
          </DialogHeader>
          {selectedFollowUpLead && (
            <div className="bg-gray-50 dark:bg-secondary rounded-lg p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600 dark:text-muted-foreground">Name:</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-foreground">{selectedFollowUpLead.name}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600 dark:text-muted-foreground">Phone:</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-foreground">{selectedFollowUpLead.phone}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600 dark:text-muted-foreground">Event:</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-foreground">{selectedFollowUpLead.eventType}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600 dark:text-muted-foreground">Follow-up Date:</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-foreground">{formatDate(selectedFollowUpLead.followupDate)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600 dark:text-muted-foreground">Status:</span>
                <Badge className={getStatusConfig(selectedFollowUpLead.status).color}>
                  {getStatusConfig(selectedFollowUpLead.status).label}
                </Badge>
              </div>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setCallDialogOpen(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleOpenStatusUpdate}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Update Status
            </Button>
            <Button
              onClick={handleCallConfirm}
              className="flex-1 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white"
            >
              <Phone className="w-4 h-4 mr-2" />
              Call Now
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Follow-up Status Update Dialog */}
      <Dialog open={statusUpdateDialogOpen} onOpenChange={setStatusUpdateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-blue-600" />
              Update Lead Status
            </DialogTitle>
            <DialogDescription>
              Update the status for {selectedFollowUpLead?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>New Status</Label>
              <Select value={newStatusForFollowUp} onValueChange={setNewStatusForFollowUp}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="in_followup">In Follow-up</SelectItem>
                  <SelectItem value="won">Won</SelectItem>
                  <SelectItem value="lost">Lost</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Progress Note (Optional)</Label>
              <Textarea
                value={followUpProgressNote}
                onChange={(e) => setFollowUpProgressNote(e.target.value)}
                placeholder="Add notes about this follow-up call..."
                rows={3}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setStatusUpdateDialogOpen(false);
                setSelectedFollowUpLead(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleFollowUpStatusUpdate}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Update Status
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Leads;